import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  FitnessCenter as FitnessCenterIcon,
  Bolt as BoltIcon,
  NotificationsActive as NotificationsActiveIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { CredentialResponse, GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { useGoogleOAuth } from '../App';

const valueProps = [
  { icon: <BoltIcon fontSize="small" />, title: 'По-бърз старт', text: 'Влизаш и виждаш приоритетите за деня веднага.' },
  { icon: <TimelineIcon fontSize="small" />, title: 'Ясен прогрес', text: 'Dashboard-ът държи програмите, последните тренировки и статистиките на едно място.' },
  { icon: <NotificationsActiveIcon fontSize="small" />, title: 'Съвети навреме', text: 'Треньорът и нотификациите остават на един tap разстояние.' },
];

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const { login, googleLogin, isLoading, error } = useAuth();
  const isGoogleOAuthAvailable = useGoogleOAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!username || !password) {
      setLocalError('Моля, въведете потребителско име и парола');
      return;
    }

    try {
      await login(username, password, rememberMe);
    } catch {
      // Error is already handled in AuthContext
    }
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    setLocalError(null);
    if (credentialResponse.credential) {
      try {
        await googleLogin(credentialResponse.credential);
      } catch {
        // Error is already handled in AuthContext
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflowX: 'hidden',
        background: 'linear-gradient(180deg, rgba(8,17,31,0.96) 0%, rgba(15,23,42,0.98) 100%)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at top left, rgba(125,211,252,0.18), transparent 30%), radial-gradient(circle at bottom right, rgba(139,255,176,0.12), transparent 28%)',
          pointerEvents: 'none',
        }}
      />
      <Container maxWidth="lg" sx={{ position: 'relative', py: { xs: 3, md: 5 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2.5, md: 3 }} alignItems="stretch">
          <Card
            elevation={0}
            sx={{
              flex: 1,
              minHeight: { xs: 'auto', md: 640 },
              display: 'flex',
              background: `linear-gradient(155deg, ${alpha('#7dd3fc', 0.18)} 0%, ${alpha('#8bffb0', 0.06)} 100%)`,
            }}
          >
            <CardContent
              sx={{
                p: { xs: 3, sm: 4, md: 5 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 4,
              }}
            >
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: '18px',
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: alpha('#08111f', 0.42),
                      color: 'primary.main',
                    }}
                  >
                    <FitnessCenterIcon />
                  </Box>
                  <Box>
                    <Typography variant="overline" sx={{ color: 'primary.light' }}>
                      Gym Assistant
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Training command center
                    </Typography>
                  </Box>
                </Stack>

                <Typography variant="h3" component="h1" gutterBottom>
                  Gym Assistant
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 480 }}>
                  Влезте в профила си и продължете от мястото, където спряхте.
                </Typography>
              </Box>

              <Stack spacing={1.5}>
                {valueProps.map((item) => (
                  <Box
                    key={item.title}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      backgroundColor: alpha('#08111f', 0.32),
                      border: `1px solid ${alpha('#cbd5e1', 0.08)}`,
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '12px',
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: alpha('#7dd3fc', 0.14),
                          color: 'primary.main',
                          flexShrink: 0,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2">{item.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ width: '100%', maxWidth: { xs: '100%', md: 460 } }}>
            <CardContent sx={{ p: { xs: 3, sm: 4 }, '&:last-child': { pb: { xs: 3, sm: 4 } } }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="overline" sx={{ color: 'primary.light' }}>
                  Добре дошъл обратно
                </Typography>
                <Typography variant="h4" gutterBottom>
                  Влезте в профила си
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Използвайте стандартния вход или Google, когато OAuth е наличен.
                </Typography>
              </Box>

              {(error || localError) && (
                <Alert severity="error" sx={{ mb: 2.5 }}>
                  {error || localError}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Потребителско име"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    autoFocus
                    disabled={isLoading}
                  />
                  <TextField
                    fullWidth
                    label="Парола"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    disabled={isLoading}
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        disabled={isLoading}
                      />
                    )}
                    label="Запомни ме за 20 дни"
                    sx={{ m: 0, alignItems: 'flex-start' }}
                  />
                  <Button type="submit" fullWidth variant="contained" size="large" disabled={isLoading}>
                    {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Вход'}
                  </Button>
                </Stack>
              </Box>

              {isGoogleOAuthAvailable && (
                <>
                  <Divider sx={{ my: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      или
                    </Typography>
                  </Divider>

                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => setLocalError('Грешка при вход с Google')}
                      theme="filled_black"
                      size="large"
                      width="360"
                      text="signin_with"
                      shape="rectangular"
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
}
